import { OTP_SENT_AT_STORAGE_KEY } from '../constants/investor-login.constants';
import type { TInvestorSession } from '../types/investor-login.types';

import { logger } from '@/lib/logger';
import { createTabStorage } from '@/shared/tab-storage/public';

const SESSION_STORAGE_KEY = 'glomo.investorPortal.session';

/**
 * `sessionStorage`, not `localStorage`: both survive a refresh, but this dies with the tab, and
 * it matches where the payload lives — so a new tab has neither and re-enters via the link.
 * The token carries an encrypted PAN and is never logged, rendered or sent anywhere but our own
 * API. The one thing that reads inside it is decodeInvestorClaims, for parent_merchant_id.
 */
const sessionStore = createTabStorage<TInvestorSession>(SESSION_STORAGE_KEY);

/**
 * Persisted, not held in the mutation: the send happens on `/login` but renders on `/otp`, a
 * different instance — and a refresh should resume the cooldown, since the code is still valid.
 */
const otpSendStore = createTabStorage<{ at: number; message: string }>(OTP_SENT_AT_STORAGE_KEY);

export const storeInvestorSession = (session: TInvestorSession): void => sessionStore.write(session);

export const getInvestorSession = (): TInvestorSession | null => sessionStore.read();

export const clearInvestorSession = (): void => sessionStore.clear();

// `expires_at` is returned explicitly precisely so the client never has to decode the JWT.
export const hasLiveInvestorSession = (): boolean => {
  const session = getInvestorSession();
  if (!session) return false;

  const expiresAt = Date.parse(session.expiresAt);
  if (Number.isNaN(expiresAt)) {
    logger.error('investor session has an unparseable expiry', { hasToken: true });
    return false;
  }

  return expiresAt > Date.now();
};

export const markOtpSent = (message: string): void => otpSendStore.write({ at: Date.now(), message });

export const getLastOtpSend = (): { at: number; message: string } | null => {
  const stored = otpSendStore.read();
  return stored && Number.isFinite(stored.at) ? stored : null;
};
